import { sameOriginEndpoint } from './clinicalSession';
import { ClinicalApi, normalizePage, StudyPage } from './clinicalApi';

export const launchFilterKeys = [
  'out_no',
  'in_no',
  'patient_id',
  'register_key',
  'study_id',
] as const;
type LaunchGrant = { token: string; expiresAt: number; registerKeys: string[] };

export class LaunchSessionService {
  private grant: LaunchGrant | null = null;
  private version = 0;
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private endpoint = '/api/clinical/launch/exchange';
  readonly api = new ClinicalApi(
    this,
    key => {
      if (!this.grant?.registerKeys.includes(key)) {
        throw new Error('此检查不在链接授权范围内。');
      }
    },
    'omit'
  );
  configure(config: Record<string, any>) {
    this.endpoint = config.launchEndpoint;
    this.api.configure({ ...config, studiesEndpoint: config.launchStudiesEndpoint });
  }
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  getSnapshot = () => this.grant;
  getVersion = () => this.version;
  getCurrentToken = () =>
    this.grant && this.grant.expiresAt > Date.now() ? this.grant.token : null;
  getAuthorizationHeader = () => {
    const token = this.getCurrentToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  ensureToken = async () => {
    const token = this.getCurrentToken();
    if (!token) {
      this.clearToken();
      throw new Error('直达链接授权已过期，请从医院系统重新打开。');
    }
    return token;
  };
  clearToken = () => {
    this.version++;
    this.grant = null;
    clearTimeout(this.timer);
    this.listeners.forEach(listener => listener());
  };
  async exchange(
    ticket: string,
    filters: Record<string, string>,
    signal: AbortSignal
  ): Promise<StudyPage> {
    this.clearToken();
    const version = this.version;
    if (!ticket || ticket.length > 4096 || !Object.keys(filters).length) {
      throw new Error('链接缺少授权凭证或检查查询条件，请从医院系统重新打开。');
    }
    if (!this.endpoint) {
      throw new Error('尚未配置直达链接授权接口，请联系管理员。');
    }
    const response = await fetch(sameOriginEndpoint(this.endpoint), {
      method: 'POST',
      credentials: 'omit',
      signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket, filters }),
    });
    if (!response.ok) {
      throw new Error(
        response.status === 401 || response.status === 403 || response.status === 410
          ? '链接无效、已使用或已过期，请从医院系统重新打开。'
          : '链接授权服务暂不可用，请重试。'
      );
    }
    const value = await response.json();
    if (signal.aborted || version !== this.version) {
      throw new DOMException('Cancelled', 'AbortError');
    }
    const expiresAt = new Date(value.expiresUtc).getTime();
    const keys = value.scope?.registerKeys;
    if (
      typeof value.accessToken !== 'string' ||
      !value.accessToken ||
      !Number.isFinite(expiresAt) ||
      expiresAt <= Date.now() ||
      !Array.isArray(keys) ||
      keys.some(key => typeof key !== 'string' || !key)
    ) {
      throw new Error('授权接口返回格式不正确。');
    }
    const result = normalizePage(value.studies);
    if (
      result.studies.some(study => !keys.includes(study.register_key)) ||
      result.total > keys.length
    ) {
      throw new Error('授权接口返回了范围外的检查。');
    }
    if (
      (filters.patient_id &&
        result.studies.some(study => study.patient_id !== filters.patient_id)) ||
      (filters.register_key &&
        (keys.some(key => key !== filters.register_key) ||
          result.studies.some(study => study.register_key !== filters.register_key))) ||
      (filters.study_id && result.studies.some(study => study.study_id !== filters.study_id))
    ) {
      throw new Error('授权接口返回了查询条件范围外的检查。');
    }
    this.grant = {
      token: value.accessToken,
      expiresAt,
      registerKeys: [...new Set(keys)] as string[],
    };
    const expire = () => {
      if (!this.grant || version !== this.version) {
        return;
      }
      const delay = expiresAt - Date.now();
      if (delay <= 0) {
        this.clearToken();
      } else {
        this.timer = setTimeout(expire, Math.min(2147483647, delay));
      }
    };
    expire();
    this.listeners.forEach(listener => listener());
    return result;
  }
}
const launchSession = new LaunchSessionService();
export default launchSession;
